import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  RoomService as VenueIcon
} from '@mui/icons-material';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PageHeader from '../common/PageHeader';
import VenueManager from './VenueManager';
import { db, functions } from '../../firebase/config';
import { selectCanManageGlobalSettings, selectCurrentUser } from '../../redux/slices/authSlice';
import brandingConfig from '../../config/brandingConfig';
import { NEXTCAB_SUPPORT } from '../../config/platformSupport';

/**
 * Einrichtungs-Assistent für den Venue-Bestellkanal (Taxi Butler).
 * Konzept: docs/general/venue_bestellknopf_taxibutler_konzept.md (§10.7)
 *
 * Besonderheit gegenüber den anderen Assistenten: Es gibt keinen Verbindungstest,
 * den wir auslösen könnten. Taxi Butler ruft unsere Endpunkte an, nicht umgekehrt —
 * Die Verbindungsprüfung zeigt deshalb, was tatsächlich bei uns angekommen ist.
 */
const TOTAL_STEPS = 6;

/**
 * Geraete und Preise. Die Preise stammen aus Taxi Butlers Auskunft vom Juli 2026
 * (Konzept §10.1) und sind deren Sache, nicht unsere: Wir verkaufen die Geraete
 * nicht, der Vertrag laeuft direkt zwischen Kunde und Taxi Butler. Sie stehen hier
 * nur zur Orientierung und sind ueberall ausdruecklich als unverbindlich
 * gekennzeichnet — aendert Taxi Butler die Liste, haben wir nichts zugesagt.
 */
const PRICE_INFO_DATE = 'Juli 2026';

const DEVICE_OPTIONS = [
  {
    value: 'one',
    label: 'Butler ONE',
    price: '69 € einmalig + 14 €/Monat (24 Monate) bzw. 17 €/Monat monatlich kündbar',
    // Frueher stand hier "ohne Ziel- oder Zeitangabe" — missverstaendlich: Gemeint
    // war, dass beim Bestellen nichts einzugeben ist. Das Display zeigt sehr wohl
    // eine Zeit, naemlich die Vorfahrzeit (§15.7).
    detail: 'Ein Knopf, 4G. Beim Bestellen ist nichts einzugeben, weder Ziel noch '
      + 'Wunschzeit. Das Display zeigt Fahrzeug, Kennzeichen und Vorfahrzeit.'
  },
  {
    value: 'plus6',
    label: 'Butler PLUS 6',
    price: '150 € einmalig + 18 €/Monat (24 Monate) bzw. 21 €/Monat monatlich kündbar',
    detail: 'Touchscreen, 4G + WLAN. Ziel, Zeit, Fahrzeugart, Fahrgastdaten, Ticketdruck.'
  },
  {
    value: 'kiosk',
    label: 'KIOSK',
    price: '950 € Kaufpreis + 25 €/Monat (24 Monate) bzw. 27 €/Monat kurze Laufzeit',
    detail: 'Standgerät für Empfangsbereiche, gleiche Funktionen wie PLUS.'
  }
];

// Kontakt für die Gerätebestellung: NextCab ist gegenüber Taxi Butler der
// Kooperationspartner (Konzept §10.7, Partner-Provision), siehe platformSupport.js.

/**
 * Fertige Mail an den NextCab-Support. Die Zugangsdaten muss Taxi Butler selbst
 * hinterlegen — ein Eingabefeld dafuer gibt es dort nicht (Konzept §15.35). Der Weg
 * fuehrt deshalb zwingend ueber uns, und diese drei Angaben duerfen dabei weder
 * verstuemmelt noch unvollstaendig ankommen: Ein falsch abgetipptes Passwort faellt
 * erst auf, wenn die erste Bestellung ins Leere laeuft.
 */
const buildCredentialsMail = ({ baseUrl, accountId, secret }) => {
  const company = brandingConfig.companyName || '';
  const subject = `Taxi-Butler-Zugangsdaten${company ? ` — ${company}` : ''}`;
  const body = [
    'Hallo,',
    '',
    'anbei unsere Zugangsdaten für die Taxi-Butler-Anbindung.',
    'Bitte bei Taxi Butler hinterlegen lassen.',
    '',
    company ? `Betrieb: ${company}` : null,
    `Adresse: ${baseUrl || '(bitte im Portal ablesen)'}`,
    `Benutzername: ${accountId}`,
    `Passwort: ${secret}`,
    '',
    'Vielen Dank!'
  ]
    .filter((line) => line !== null)
    .join('\n');
  return { subject, body, text: `Betreff: ${subject}\n\n${body}` };
};

/**
 * Zwei Wege, bewusst beide: Der Knopf legt die fertige Mail in die Zwischenablage
 * (funktioniert auch dort, wo kein Mailprogramm eingerichtet ist), der Link oeffnet
 * sie direkt. Beide enthalten dasselbe.
 */
const CredentialsMailActions = ({ baseUrl, accountId, secret, onCopy }) => {
  const mail = buildCredentialsMail({ baseUrl, accountId, secret });
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap">
      <Button size="small" variant="outlined" onClick={() => onCopy(mail.text)}>
        Mailvorlage kopieren
      </Button>
      <Button
        size="small"
        component={Link}
        href={`mailto:${NEXTCAB_SUPPORT.email}?subject=${encodeURIComponent(mail.subject)}`
          + `&body=${encodeURIComponent(mail.body)}`}
      >
        Mail öffnen
      </Button>
      <Typography variant="caption" color="text.secondary">
        an {NEXTCAB_SUPPORT.email}
      </Typography>
    </Stack>
  );
};

const VenueChannelSetupGuidePage = () => {
  const navigate = useNavigate();
  const canManage = useSelector(selectCanManageGlobalSettings);
  // Die Regeln geben Venues nur im eigenen Mandanten frei — die ID muss deshalb
  // vom angemeldeten Konto kommen und darf nicht fest verdrahtet sein.
  const currentUser = useSelector(selectCurrentUser);
  const organizationId = currentUser?.organizationId || 'default_organization';

  const [activeStep, setActiveStep] = useState(0);
  const [infoConfirmed, setInfoConfirmed] = useState(false);
  // Bis der gespeicherte Stand da ist, nichts zurueckschreiben — sonst ueberschriebe
  // der leere Ausgangszustand die Bestaetigungen beim Oeffnen der Seite.
  const [setupStateLoaded, setSetupStateLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Geräte — Ausgangslage des Betriebs
  const [fleetMode, setFleetMode] = useState('');
  // Nur bei vorhandenen Geraeten: Sind sie schon an ein anderes Dispositionssystem
  // angebunden ('migration') oder fabrikneu und noch nirgends angemeldet ('fresh')?
  // Die beiden Faelle laufen bei der Kundenreferenz in entgegengesetzte Richtungen.
  const [deviceSetup, setDeviceSetup] = useState('');
  // Die Geraetezuordnung passiert im Taxi-Butler-Portal und ist von hier aus nicht
  // pruefbar. Der Nutzer bestaetigt selbst, dass er dort fertig ist.
  const [devicesLinked, setDevicesLinked] = useState(false);

  // Zugangsdaten
  // Kein Schalter mehr in der Oberflaeche: Der Kanal ist offen, sobald ein
  // Bestellort steht. Nur ein ausdrueckliches `enabled: false` in Firestore sperrt
  // ihn — das wird angezeigt, damit der Support nicht im Dunkeln sucht.
  const [channelDisabled, setChannelDisabled] = useState(false);
  const [freshCredentials, setFreshCredentials] = useState(null);
  // Erneut angezeigte Passwoerter, je Benutzername. Nur der NextCab-Support bekommt sie
  // vom Server; fuer den Betreiber-Admin bleibt es bei der Einmal-Anzeige.
  const [revealedSecrets, setRevealedSecrets] = useState({});
  // Zurueckgezogene Konten bleiben als Nachweis erhalten, verstopfen aber die Liste.
  const [showRevokedAccounts, setShowRevokedAccounts] = useState(false);

  // Bestellorte
  const [venues, setVenues] = useState([]);

  // Verbindungspruefung
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'venueChannel'),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setChannelDisabled(data.enabled === false);
        // Aus Altbestaenden nur die Ausgangslage uebernehmen — die uebrigen Felder
        // der frueheren Geraete-Anfrage werden nicht mehr gepflegt.
        if (data.fleetRequest?.mode) {
          setFleetMode(data.fleetRequest.mode === 'existing' ? 'existing' : 'new');
        }
        // Die drei selbst bestaetigten Schritte. Ohne sie stand der Assistent nach
        // jedem Neuladen wieder bei Schritt 1, obwohl die Einrichtung laengst lief.
        const setup = data.setupState || {};
        setInfoConfirmed(setup.infoConfirmed === true);
        setDevicesLinked(setup.devicesLinked === true);
        if (typeof setup.fleetMode === 'string' && setup.fleetMode) {
          setFleetMode(setup.fleetMode);
        }
        if (typeof setup.deviceSetup === 'string') {
          setDeviceSetup(setup.deviceSetup);
        }
        setSetupStateLoaded(true);
      },
      () => undefined
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      // Die Rules geben Venues nur im eigenen Mandanten frei. Eine Abfrage ohne
      // diesen Filter wird deshalb komplett abgelehnt — Firestore-Rules filtern
      // nicht, sie pruefen, ob die Abfrage ausschliesslich Erlaubtes treffen kann.
      query(collection(db, 'venues'), where('organizationId', '==', organizationId)),
      (snapshot) => setVenues(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) =>
        setError(
          `Bestellorte konnten nicht geladen werden: ${err?.message || 'unbekannter Fehler'}`
        )
    );
    return unsubscribe;
  }, [organizationId]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const callable = httpsCallable(functions, 'getVenueChannelStatus');
      const result = await callable({});
      setStatus(result.data);
    } catch (err) {
      setError(err?.message || 'Status konnte nicht geladen werden.');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeStep >= 2) loadStatus();
  }, [activeStep, loadStatus]);

  const hasCredentials = useMemo(
    () => Boolean(status?.accounts?.some((a) => a.enabled)),
    [status]
  );

  const revokedAccountCount = useMemo(
    () => (status?.accounts || []).filter((a) => !a.enabled).length,
    [status]
  );

  const visibleAccounts = useMemo(() => {
    const all = status?.accounts || [];
    return showRevokedAccounts ? all : all.filter((a) => a.enabled);
  }, [status, showRevokedAccounts]);

  /**
   * Welches Konto Taxi Butler tatsaechlich benutzt, koennen wir nicht erfragen —
   * sie rufen uns an, nicht umgekehrt. Der einzige belastbare Hinweis ist der
   * juengste Zugriff: Wer sich zuletzt gemeldet hat, ist das hinterlegte Konto.
   */
  const accountInUseId = useMemo(() => {
    let best = null;
    for (const account of status?.accounts || []) {
      if (!account.lastRequestAt) continue;
      if (!best || new Date(account.lastRequestAt) > new Date(best.lastRequestAt)) {
        best = account;
      }
    }
    return best?.accountId || null;
  }, [status]);

  const hasUnusedAccount = useMemo(
    () => (status?.accounts || []).some((a) => a.enabled && !a.lastRequestAt),
    [status]
  );

  const completedCount = useMemo(() => {
    let count = 0;
    if (infoConfirmed) count += 1;
    // Bei "neue Geraete" gibt es nichts weiter zu entscheiden — die Wahl selbst ist
    // der Schritt. Bei vorhandenen Geraeten fehlt noch die Ausgangslage.
    if (fleetMode === 'new' || (fleetMode === 'existing' && deviceSetup)) count += 1;
    if (hasCredentials) count += 1;
    if (venues.length > 0) count += 1;
    if (devicesLinked) count += 1;
    if (status?.recentRequests?.length > 0) count += 1;
    return Math.min(count, TOTAL_STEPS);
  }, [
    infoConfirmed,
    fleetMode,
    deviceSetup,
    hasCredentials,
    venues.length,
    devicesLinked,
    status
  ]);

  const runCallable = async (name, payload) => {
    setBusy(true);
    setError(null);
    try {
      const callable = httpsCallable(functions, name);
      const result = await callable(payload);
      return result.data;
    } catch (err) {
      setError(err?.message || 'Die Aktion ist fehlgeschlagen. Bitte erneut versuchen.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Die drei selbst bestaetigten Schritte ablegen. Bewusst ohne `busy`/Fehler-
   * Banner: Ein Haken soll nicht die halbe Seite sperren, und schlaegt der Schreib-
   * vorgang fehl, ist der Schaden ein vergessener Haken und keine verlorene
   * Einrichtung. Der Fehler steht in der Konsole, der naechste Klick versucht es neu.
   */
  const persistSetupState = (patch) => {
    if (!setupStateLoaded) return;
    httpsCallable(functions, 'configureVenueChannel')({ setupState: patch }).catch((err) => {
      console.warn('[venue:setup] Stand konnte nicht gespeichert werden', err);
    });
  };

  const handleCreateCredentials = async () => {
    const result = await runCallable('createVenueApiCredentials', { label: 'Taxi Butler' });
    if (result) {
      setFreshCredentials(result);
      loadStatus();
    }
  };

  const handleReveal = async (accountId) => {
    const result = await runCallable('revealVenueApiCredentials', { accountId });
    if (result?.secret) {
      setRevealedSecrets((prev) => ({ ...prev, [accountId]: result.secret }));
      loadStatus();
    }
  };

  const handleRevoke = async (accountId) => {
    const result = await runCallable('revokeVenueApiCredentials', { accountId });
    if (result) {
      setFreshCredentials(null);
      setRevealedSecrets((prev) => {
        const next = { ...prev };
        delete next[accountId];
        return next;
      });
      loadStatus();
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  if (!canManage) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <PageHeader
          title="Bestellknopf einrichten — Taxi Butler"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Einstellungen', path: '/einstellungen' },
            { label: 'Bestellknopf einrichten' }
          ]}
        />
        <Alert severity="info">
          Der Venue-Bestellkanal wird von der Hauptzentrale verwaltet. Bitte wenden
          Sie sich an Ihre Zentrale.
        </Alert>
      </Container>
    );
  }

  const navigation = (canContinue = true) => (
    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
      <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)} disabled={!canContinue}>
        Weiter
      </Button>
      {activeStep > 0 && <Button onClick={() => setActiveStep((s) => s - 1)}>Zurück</Button>}
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <PageHeader
        title="Bestellknopf einrichten — Taxi Butler"
        description="Bestellgeräte von Taxi Butler in Hotels, Restaurants und Kliniken an Ihre Disposition anbinden"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Einstellungen', path: '/einstellungen' },
          { label: 'Bestellknopf einrichten' }
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <VenueIcon color="primary" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {completedCount} von {TOTAL_STEPS} Schritten erledigt
            </Typography>
            {/* Es gibt keinen Schalter mehr — der Kanal ist offen. Gezeigt wird
                nur der Ausnahmefall: die Notbremse in Firestore. */}
            {channelDisabled && (
              <Chip size="small" color="error" label="Kanal abgeschaltet" />
            )}
          </Stack>
          <LinearProgress variant="determinate" value={(completedCount / TOTAL_STEPS) * 100} />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* 1 — Info */}
          <Step>
            <StepLabel onClick={() => setActiveStep(0)} sx={{ cursor: 'pointer' }}>
              Was ist ein Bestellknopf?
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Ein kleines Gerät am Tresen: Das Personal im Hotel, Restaurant oder in
                der Klinik drückt einen Knopf, und die Bestellung landet direkt in Ihrer
                Disposition — ohne Anruf. Die Geräte stammen von Taxi Butler; den Vertrag
                schließen Sie direkt mit Taxi Butler, über NextCab entstehen keine
                Zusatzkosten.
              </Typography>
              <Table size="small" sx={{ mb: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Gerät</TableCell>
                    <TableCell>Preis (unverbindlich)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DEVICE_OPTIONS.map((option) => (
                    <TableRow key={option.value}>
                      <TableCell>
                        <strong>{option.label}</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {option.detail}
                        </Typography>
                      </TableCell>
                      <TableCell>{option.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Alert severity="info" sx={{ mb: 1 }}>
                <strong>Alle Angaben unverbindlich.</strong> Preise, Laufzeiten und
                Leistungen legt Taxi Butler fest und kann sie jederzeit ändern; der
                Vertrag kommt direkt zwischen Ihnen und Taxi Butler zustande. Die
                Angaben hier geben den Stand von {PRICE_INFO_DATE} wieder und dienen
                nur der Orientierung — verbindlich ist allein das Angebot von Taxi
                Butler. Zuletzt genannt: Preise zzgl. MwSt. und Versand, enthalten
                SIM-Karte, Support, Einweisung und ein Jahr Garantie mit
                Sofortaustausch, Testzeitraum vier Wochen.
              </Alert>
              <FormControlLabel
                sx={{ display: 'block', mt: 1 }}
                control={
                  <Checkbox
                    checked={infoConfirmed}
                    onChange={(e) => {
                      setInfoConfirmed(e.target.checked);
                      persistSetupState({ infoConfirmed: e.target.checked });
                    }}
                  />
                }
                label="Verstanden — weiter zur Anmeldung"
              />
              {navigation(infoConfirmed)}
            </StepContent>
          </Step>

          {/* 2 — Flotte anmelden */}
          <Step>
            <StepLabel onClick={() => setActiveStep(1)} sx={{ cursor: 'pointer' }}>
              Geräte
            </StepLabel>
            <StepContent>
              <RadioGroup
                value={fleetMode}
                onChange={(e) => {
                  setFleetMode(e.target.value);
                  persistSetupState({ fleetMode: e.target.value });
                }}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label="Wir haben bereits Taxi-Butler-Geräte"
                />
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Wir haben noch keine Geräte"
                />
              </RadioGroup>

              {fleetMode === '' ? (
                <Typography variant="body2" color="text.secondary">
                  Bitte wählen Sie aus, wie es bei Ihnen aussieht.
                </Typography>
              ) : fleetMode === 'new' ? (
                /* Ohne Geräte gibt es hier nichts einzurichten. Frueher stand an dieser
                   Stelle ein Bestellformular — es hat die Angaben nur in die Datenbank
                   geschrieben, ohne dass jemand davon erfahren haette. Statt eines
                   Versprechens, das die Software nicht haelt, der direkte Weg. */
                <Alert severity="info">
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Die Geräte bestellen Sie über den NextCab-Support — wir melden Sie
                    bei Taxi Butler an und richten alles Weitere mit Ihnen ein. Die
                    Preise finden Sie im Schritt „Was ist ein Bestellknopf?“.
                  </Typography>
                  <Typography variant="body2">
                    <strong>E-Mail:</strong>{' '}
                    <Link href={`mailto:${NEXTCAB_SUPPORT.email}`}>
                      {NEXTCAB_SUPPORT.email}
                    </Link>
                    {' · '}
                    <strong>Telefon:</strong>{' '}
                    <Link href={`tel:${NEXTCAB_SUPPORT.phone.replace(/\s/g, '')}`}>
                      {NEXTCAB_SUPPORT.phone}
                    </Link>
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Sobald die Geräte da sind, kommen Sie hierher zurück und wählen
                    „Wir haben bereits Taxi-Butler-Geräte".
                  </Typography>
                </Alert>
              ) : (
                <>
                  <RadioGroup
                    value={deviceSetup}
                    onChange={(e) => {
                      setDeviceSetup(e.target.value);
                      persistSetupState({ deviceSetup: e.target.value });
                    }}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel
                      value="fresh"
                      control={<Radio />}
                      label="Die Geräte sind neu und noch nicht angebunden"
                    />
                    <FormControlLabel
                      value="migration"
                      control={<Radio />}
                      label="Die Geräte laufen bisher auf ein anderes Dispositionssystem"
                    />
                  </RadioGroup>

                  {deviceSetup === '' && (
                    <Typography variant="body2" color="text.secondary">
                      Bitte wählen Sie aus, wie Ihre Geräte heute genutzt werden.
                    </Typography>
                  )}

                  {deviceSetup === 'migration' && (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Ihre Geräte und Ihr Vertrag bleiben, wie sie sind. Umgestellt
                        wird nur, wohin Taxi Butler die Bestellungen schickt — bisher an
                        Ihr altes System, künftig an NextCab.
                      </Alert>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        So läuft die Umstellung
                      </Typography>
                      <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Im nächsten Schritt erzeugen Sie die Zugangsdaten: Adresse,
                          Benutzername und Passwort.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Diese drei Angaben schicken Sie zusammen mit Ihrem Firmennamen
                          bei Taxi Butler per E-Mail an{' '}
                          <Link href={`mailto:${NEXTCAB_SUPPORT.email}`}>
                            {NEXTCAB_SUPPORT.email}
                          </Link>
                          . Wir lassen sie bei Taxi Butler eintragen.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Taxi Butler stellt Ihre Company um — an den Geräten selbst
                          ändert sich nichts.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Sie legen unter „Bestellorte anlegen“ Ihre Bestellorte an und
                          übernehmen dabei die Kundenreferenzen aus dem Taxi-Butler-Portal.
                        </Typography>
                        <Typography component="li" variant="body2">
                          Eine Testbestellung am Gerät zeigt unter „Verbindung prüfen“,
                          dass alles ankommt.
                        </Typography>
                      </Box>
                      <Alert severity="warning">
                        <strong>Wichtig für „Bestellorte anlegen“:</strong> Ihre Bestellorte sind bei
                        Taxi Butler bereits angelegt. Übernehmen Sie dort die vorhandene
                        Kundenreferenz — dann muss an Ihren Geräten nichts
                        umkonfiguriert werden. Sie steht im Taxi-Butler-Portal beim
                        jeweiligen Abholort im Feld <strong>„Kundenreferenz"</strong>
                        {' '}(in der Schnittstelle „customerReference"); falls dort keine
                        hinterlegt ist, ordnen wir über die Seriennummer des Geräts zu.
                      </Alert>
                    </>
                  )}

                  {deviceSetup === 'fresh' && (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Es gibt nichts umzustellen — die Geräte werden einmal
                        eingerichtet und melden von Anfang an bei NextCab.
                      </Alert>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        So richten Sie die Geräte ein
                      </Typography>
                      <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Im nächsten Schritt erzeugen Sie die Zugangsdaten: Adresse,
                          Benutzername und Passwort.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Diese drei Angaben schicken Sie zusammen mit Ihrem Firmennamen
                          bei Taxi Butler per E-Mail an{' '}
                          <Link href={`mailto:${NEXTCAB_SUPPORT.email}`}>
                            {NEXTCAB_SUPPORT.email}
                          </Link>
                          . Wir lassen sie bei Taxi Butler eintragen.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Sie legen unter „Bestellorte anlegen“ einen Bestellort je Gerät
                          an, mit der Adresse, an der das Gerät steht.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                          Unter „Geräte im Taxi-Butler-Portal zuordnen“ legen Sie den
                          Abholort an und tragen dort unsere Kundenreferenz im Feld
                          „Kundenreferenz“ ein. Erst danach fragt das Taxi-Butler-Portal
                          den Aktivierungscode ab, den das Gerätedisplay zeigt.
                        </Typography>
                        <Typography component="li" variant="body2">
                          Eine Testbestellung am Gerät zeigt unter „Verbindung prüfen“,
                          dass alles ankommt.
                        </Typography>
                      </Box>
                      <Alert severity="warning">
                        <strong>Wichtig für „Bestellorte anlegen“:</strong> Hier läuft die Kundenreferenz
                        andersherum als bei einer Umstellung — <strong>wir</strong>
                        {' '}vergeben sie, und Sie tragen sie im Taxi-Butler-Portal beim
                        Abholort in das Feld <strong>„Kundenreferenz"</strong> ein
                        {' '}(in der Schnittstelle „customerReference"). Legen Sie den
                        Bestellort deshalb zuerst bei uns an. Im Taxi-Butler-Portal heißt das, was
                        wir Bestellort nennen, <strong>Abholort</strong>.
                      </Alert>
                    </>
                  )}
                </>
              )}
              {navigation(fleetMode === 'existing' && deviceSetup !== '')}
            </StepContent>
          </Step>

          {/* 3 — Technische Kopplung */}
          <Step>
            <StepLabel onClick={() => setActiveStep(2)} sx={{ cursor: 'pointer' }}>
              Zugangsdaten erzeugen
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Taxi Butler braucht eine Adresse und ein Passwort, um Bestellungen an
                Ihre Disposition zu übergeben. Beides erzeugen Sie hier. Schicken Sie
                die drei Angaben anschließend an{' '}
                <Link href={`mailto:${NEXTCAB_SUPPORT.email}`}>
                  {NEXTCAB_SUPPORT.email}
                </Link>{' '}
                — wir lassen sie bei Taxi Butler eintragen; im Portal dort gibt es
                dafür kein Eingabefeld. Direkt nach dem Erzeugen steht eine fertige
                Mail zum Kopieren bereit, damit keine der Angaben verlorengeht.
              </Typography>

              {/* Die Adresse ist kein Geheimnis und gehoert dauerhaft sichtbar: Sie
                  stand bisher nur in der Einmal-Anzeige und war nach einem Reload
                  nicht mehr auffindbar. Sie bricht um, statt abgeschnitten zu werden —
                  wer sie vorliest oder abtippt, braucht sie vollstaendig. */}
              {status?.baseUrl && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Adresse (für alle Bestellorte dieselbe)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        {status.baseUrl}
                      </Typography>
                    </Box>
                    <Tooltip title="Kopieren">
                      <IconButton size="small" onClick={() => copy(status.baseUrl)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              )}

              {freshCredentials && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {status?.canRevealSecrets
                      ? 'Zugangsdaten erzeugt — als Support können Sie das Passwort '
                        + 'später in der Liste erneut anzeigen.'
                      : 'Das Passwort wird nur dieses eine Mal angezeigt.'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Zusammen mit der Adresse oben sind das die drei Angaben für
                    Taxi Butler.
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      ['Benutzername', freshCredentials.accountId],
                      ['Passwort', freshCredentials.secret]
                    ].map(([label, value]) => (
                      <Stack key={label} direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ minWidth: 110 }}>
                          {label}
                        </Typography>
                        <TextField
                          value={value}
                          size="small"
                          fullWidth
                          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                        <Tooltip title="Kopieren">
                          <IconButton size="small" onClick={() => copy(value)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                  <CredentialsMailActions
                    baseUrl={status?.baseUrl}
                    accountId={freshCredentials.accountId}
                    secret={freshCredentials.secret}
                    onCopy={copy}
                  />
                </Alert>
              )}

              {status?.accounts?.length > 0 && (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Benutzername</TableCell>
                      <TableCell>Erstellt</TableCell>
                      <TableCell>Letzter Zugriff</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAccounts.map((account) => (
                      <React.Fragment key={account.accountId}>
                        <TableRow>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {account.accountId}
                            {!account.enabled && (
                              <Chip size="small" label="zurückgezogen" sx={{ ml: 1 }} />
                            )}
                            {account.enabled && account.accountId === accountInUseId && (
                              <Tooltip title="Von diesem Konto kam die letzte Anfrage — es ist bei Taxi Butler hinterlegt">
                                <Chip
                                  size="small"
                                  color="success"
                                  label="in Benutzung"
                                  sx={{ ml: 1 }}
                                />
                              </Tooltip>
                            )}
                            {account.enabled && !account.lastRequestAt && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label="noch nicht benutzt"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {account.createdAt
                              ? new Date(account.createdAt).toLocaleDateString('de-DE')
                              : '–'}
                          </TableCell>
                          <TableCell>
                            {account.lastRequestAt
                              ? new Date(account.lastRequestAt).toLocaleString('de-DE')
                              : 'noch keiner'}
                          </TableCell>
                          <TableCell align="right">
                            {/* Erneut anzeigen kann nur der NextCab-Support; der Server
                                prueft das noch einmal selbst. */}
                            {status.canRevealSecrets && account.enabled && (
                              <Tooltip
                                title={
                                  account.secretAvailable
                                    ? 'Passwort erneut anzeigen (Support)'
                                    : 'Vor der Umstellung erzeugt — nicht wiederherstellbar'
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    onClick={() => handleReveal(account.accountId)}
                                    disabled={busy || !account.secretAvailable}
                                  >
                                    Passwort anzeigen
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            {account.enabled && (
                              <Button size="small" color="error" onClick={() => handleRevoke(account.accountId)}>
                                Zurückziehen
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {revealedSecrets[account.accountId] && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ pt: 0 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" sx={{ minWidth: 110 }}>
                                  Passwort
                                </Typography>
                                <TextField
                                  value={revealedSecrets[account.accountId]}
                                  size="small"
                                  fullWidth
                                  InputProps={{
                                    readOnly: true,
                                    sx: { fontFamily: 'monospace', fontSize: '0.75rem' }
                                  }}
                                />
                                <Tooltip title="Kopieren">
                                  <IconButton
                                    size="small"
                                    onClick={() => copy(revealedSecrets[account.accountId])}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    setRevealedSecrets((prev) => {
                                      const next = { ...prev };
                                      delete next[account.accountId];
                                      return next;
                                    })
                                  }
                                >
                                  Ausblenden
                                </Button>
                              </Stack>
                              <CredentialsMailActions
                                baseUrl={status?.baseUrl}
                                accountId={account.accountId}
                                secret={revealedSecrets[account.accountId]}
                                onCopy={copy}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Zurueckgezogene Konten bleiben als Nachweis erhalten, gehoeren aber
                  nicht dauerhaft in die Liste — sonst steht nach ein paar Jahren
                  Betrieb mehr Altes darin als Gueltiges. */}
              {revokedAccountCount > 0 && (
                <Button
                  size="small"
                  sx={{ mb: 2 }}
                  onClick={() => setShowRevokedAccounts((v) => !v)}
                >
                  {showRevokedAccounts
                    ? 'Zurückgezogene ausblenden'
                    : `${revokedAccountCount} zurückgezogene${revokedAccountCount === 1 ? 's' : ''} Konto${revokedAccountCount === 1 ? '' : 'en'} anzeigen`}
                </Button>
              )}

              {hasUnusedAccount && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Welches Konto bei Taxi Butler hinterlegt ist, sehen wir erst an der
                  ersten Anfrage — Taxi Butler ruft uns an, nicht umgekehrt. Bis dahin
                  steht ein Konto auf „noch nicht benutzt“. Bleibt es nach der ersten
                  Bestellung dabei, sind dort andere Zugangsdaten hinterlegt als die,
                  die Sie erwarten.
                </Alert>
              )}

              <Button variant="contained" onClick={handleCreateCredentials} disabled={busy}>
                {busy ? <CircularProgress size={20} /> : 'Zugangsdaten erzeugen'}
              </Button>
              {navigation(hasCredentials)}
            </StepContent>
          </Step>

          {/* 4 — Venues */}
          <Step>
            <StepLabel onClick={() => setActiveStep(3)} sx={{ cursor: 'pointer' }}>
              Bestellorte anlegen
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Jedes Gerät gehört zu einem Bestellort. Die Adresse hier ist der
                Abholort — sie zählt für die Disposition, deshalb sollte sie genau
                stimmen. Die Kundenreferenz jedes Bestellorts tragen wir bei Taxi Butler als
                „customerReference" ein.
              </Typography>

              {/* Die Fahrzeugart ist die einzige Angabe, die auf beiden Seiten
                  zeichengenau uebereinstimmen muss. Sie gehoert deshalb hier hin,
                  bevor im naechsten Schritt der Abholort im fremden Portal entsteht
                  — nicht umgekehrt (§15.8). */}
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Hängt am Bestellort ein <strong>PLUS 6</strong> oder ein
                {' '}<strong>KIOSK</strong>, tragen Sie hier auch die Fahrzeugarten ein.
                Die Bezeichner müssen im Taxi-Butler-Portal zeichengenau genauso
                lauten; welche das sind, zeigt in jeder Zeile das Symbol
                {' '}<strong>„Angaben für das Taxi Butler Portal"</strong>. Ein
                {' '}<strong>ONE</strong> braucht davon nichts — er bestellt ohne Auswahl,
                und es gilt der Standard-Fahrzeugtyp des Bestellorts.
              </Typography>

              <VenueManager
                venues={venues}
                organizationId={organizationId}
                onError={setError}
              />

              {navigation(venues.length > 0)}
            </StepContent>
          </Step>

          {/* 5 — Geräte im Taxi-Butler-Portal */}
          <Step>
            <StepLabel onClick={() => setActiveStep(4)} sx={{ cursor: 'pointer' }}>
              Geräte im Taxi-Butler-Portal zuordnen
            </StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Dieser Schritt passiert in Ihrem Taxi-Butler-Portal, nicht hier. Dort
                heißt ein Bestellort <strong>Abholort</strong> — gemeint ist dasselbe.
              </Typography>

              {deviceSetup === 'migration' ? (
                <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                    Öffnen Sie im Taxi-Butler-Portal den jeweiligen Abholort.
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                    Vergleichen Sie den dort im Feld „Kundenreferenz" hinterlegten
                    Wert mit dem unten. Weichen sie ab, übernehmen Sie den vorhandenen
                    bei uns im Bestellort — dann muss am Gerät nichts geändert werden.
                  </Typography>
                  <Typography component="li" variant="body2">
                    Notieren Sie die Fahrzeugart-Kennungen des Abholorts und tragen Sie
                    sie im Bestellort ein, sonst landet jede Bestellung auf dem
                    Standard-Fahrzeugtyp.
                  </Typography>
                </Box>
              ) : (
                <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                    Legen Sie im Taxi-Butler-Portal unter „Abholort hinzufügen" einen
                    Abholort je Gerät an. Tippen Sie den Namen ein und wählen Sie ihn
                    aus den Vorschlägen — Adresse, Koordinaten, Telefon und Zeitzone
                    füllt Taxi Butler damit selbst.
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                    „Name im Display" ist der Text, den das Gerät zeigt; „Info für den
                    Fahrer" erreicht uns als Hinweis an der Fahrt.
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                    Tragen Sie unsere Kundenreferenz aus der Liste unten in das Feld
                    {' '}<strong>„Kundenreferenz"</strong> ein — es steht am Ende des
                    Formulars unter „Maximum gleichzeitiger Bestellungen". Bleibt es
                    leer, ordnen wir über die <strong>Seriennummer</strong> aus dem
                    Bestellort zu.
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
                    Bei einem <strong>PLUS 6</strong> oder <strong>KIOSK</strong>: Im
                    Abschnitt <strong>„Taxi Butler PLUS Options"</strong> zuerst
                    {' '}<strong>„Enabled" auf JA</strong> stellen — erst dann erscheinen
                    Fahrzeugart, Wunschzeit, Ziel und Fahrgastdaten. In das
                    Konfigurationsfeld <em>vehicleType</em> gehört je Zeile der
                    Bezeichner aus dem Bestellort — die Tabelle unten führt sie zum
                    Kopieren. Die Zahlart können Sie leer lassen. Ein
                    {' '}<strong>ONE</strong> hat diesen Abschnitt nicht.
                  </Typography>
                  <Typography component="li" variant="body2">
                    Speichern Sie den Abholort. Taxi Butler fragt danach „Möchten Sie
                    einen neuen Butler hinzufügen?" — mit <strong>Ja</strong> geht es
                    zum Ankoppeln, später erreichen Sie es über den Abholort und
                    {' '}<strong>„Butler ankoppeln"</strong>. Verlangt wird der
                    {' '}<strong>Registrierungsschlüssel</strong> im Format XX-XX-XX,
                    den nur das <strong>Display</strong> des eingeschalteten Geräts
                    zeigt — nicht die Rückseite. Zeigt das Gerät keinen, hängt es noch
                    an einem anderen Abholort: dort abmelden, Strom aus und wieder an.
                  </Typography>
                </Box>
              )}

              {venues.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Legen Sie zuerst unter „Bestellorte anlegen“ einen Bestellort an.
                </Alert>
              ) : (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bestellort</TableCell>
                      <TableCell>Kundenreferenz</TableCell>
                      <TableCell>TB-Feld „Längengrad“</TableCell>
                      <TableCell>TB-Feld „Breitengrad“</TableCell>
                      <TableCell>TB-Feld „vehicleType“</TableCell>
                      <TableCell>Seriennummer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {venues.map((venue) => (
                      <TableRow key={venue.id}>
                        <TableCell>
                          <strong>{venue.name}</strong>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {venue.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {venue.id}
                            </Typography>
                            <IconButton size="small" onClick={() => copy(venue.id)}>
                              <CopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        {/*
                          Getrennte Spalten statt eines „lat, lng"-Paars, und
                          benannt nach der Beschriftung im Taxi-Butler-Formular:
                          Dessen erstes Koordinatenfeld heisst dort "Laengengrad",
                          nimmt aber die geografische Breite (belegt durch die
                          Autovervollstaendigung des Portals selbst, §15.25). Wer
                          nach der Beschriftung geht, vertauscht beide Werte — und
                          weil beide plausibel aussehen, faellt es erst auf, wenn
                          die Bestellung im falschen Ort ankommt.
                        */}
                        <TableCell>
                          {venue.latitude != null ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {venue.latitude}
                              </Typography>
                              <IconButton size="small" onClick={() => copy(`${venue.latitude}`)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Stack>
                          ) : (
                            '–'
                          )}
                        </TableCell>
                        <TableCell>
                          {venue.longitude != null ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {venue.longitude}
                              </Typography>
                              <IconButton size="small" onClick={() => copy(`${venue.longitude}`)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Stack>
                          ) : (
                            '–'
                          )}
                        </TableCell>
                        {/*
                          Nur fuer PLUS und KIOSK von Belang; beim ONE bleibt die
                          Zelle leer, statt eine Einstellung vorzugaukeln, die es am
                          Geraet nicht gibt.
                        */}
                        <TableCell>
                          {Object.keys(venue.carTypeMap || {}).length > 0 ? (
                            <Stack spacing={0.25}>
                              {Object.keys(venue.carTypeMap).map((identifier) => (
                                <Stack key={identifier} direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                    {identifier}
                                  </Typography>
                                  <IconButton size="small" onClick={() => copy(identifier)}>
                                    <CopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Stack>
                              ))}
                            </Stack>
                          ) : (
                            '–'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {(venue.butlerSerialNos || []).join(', ') || '–'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Alert severity="warning" sx={{ mb: 1 }}>
                Die Koordinaten brauchen Sie nur, wenn die Adresse in den Vorschlägen
                fehlt. Dann gilt: Die beiden Felder sind im Taxi-Butler-Portal
                vertauscht beschriftet — „Längengrad“ nimmt dort die geografische
                <strong> Breite</strong>. Die Spalten oben sind bereits so sortiert.
                Alternativ füllt „RECALCULATE COORDINATES“ sie aus der eingetippten
                Adresse.
              </Alert>

              <Alert severity="info">
                Wir erkennen den Bestellort an der Kundenreferenz, sonst an der Seriennummer
                des Geräts und zuletzt an den Koordinaten (im Umkreis von 150 Metern).
                Einer der drei Wege muss stimmen — sonst weisen wir die Bestellung ab,
                statt sie irgendwo zuzuordnen.
              </Alert>

              <FormControlLabel
                sx={{ display: 'block', mt: 1 }}
                control={
                  <Checkbox
                    checked={devicesLinked}
                    onChange={(e) => {
                      setDevicesLinked(e.target.checked);
                      persistSetupState({ devicesLinked: e.target.checked });
                    }}
                  />
                }
                label="Geräte sind im Taxi-Butler-Portal zugeordnet"
              />
              {navigation(devicesLinked)}
            </StepContent>
          </Step>

          {/* 6 — Verbindungstest */}
          <Step>
            <StepLabel onClick={() => setActiveStep(5)} sx={{ cursor: 'pointer' }}>
              Verbindung prüfen
            </StepLabel>
            <StepContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Wir können die Verbindung nicht von uns aus anstoßen — Taxi Butler ruft
                uns an, nicht umgekehrt. Lösen Sie deshalb am Gerät eine Testbestellung
                aus; sie erscheint dann hier in der Liste.
              </Alert>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadStatus}
                  disabled={statusLoading}
                >
                  Aktualisieren
                </Button>
                {statusLoading && <CircularProgress size={16} />}
              </Stack>

              {status?.recentRequests?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zeitpunkt</TableCell>
                      <TableCell>Vorgang</TableCell>
                      <TableCell>Ergebnis</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {status.recentRequests.map((entry, index) => (
                      <TableRow key={`${entry.at}-${index}`}>
                        <TableCell>
                          {entry.at ? new Date(entry.at).toLocaleString('de-DE') : '–'}
                        </TableCell>
                        <TableCell>
                          {entry.method === 'POST' ? 'Bestellung' : 'Storno'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={entry.ok ? 'success' : 'error'}
                            label={entry.ok ? entry.detail || 'erfolgreich' : entry.detail || 'fehlgeschlagen'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Alert severity="warning">
                  Bisher ist keine Bestellung eingegangen.
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Eingehende Bestellungen erscheinen in der Fahrtverwaltung und auf der
                LiveMap mit dem Namen des Bestellorts; in der Fahrt-Detailansicht steht
                zusätzlich „Taxi Butler" als Anbieter des Geräts. Einzelne Geräte
                schalten Sie im Taxi-Butler-Portal an und ab — bei uns ist dafür nichts
                einzustellen.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => navigate('/einstellungen')}>
                  Fertig
                </Button>
                <Button onClick={() => setActiveStep((s) => s - 1)}>Zurück</Button>
              </Stack>
            </StepContent>
          </Step>

        </Stepper>
      </Paper>

    </Container>
  );
};

export default VenueChannelSetupGuidePage;
